import {atom, type Setter, type WritableAtom} from "jotai";
import {atomWithStorage, createJSONStorage} from "jotai/utils";
import {resolveSetterVal, type SetterVal} from "../setter/Setter";

type DeepPartial<T> = { [attr in keyof T]?: T[attr] extends object ? DeepPartial<T[attr]> : T[attr] }
type Callback<T> = (set: Setter, newValue: T) => void
type AtomDependency<T> = (callback: Callback<T>) => void

function copy<T>(value: T): T {
    if(typeof value === "object") {
        if(Array.isArray(value)) return value.map(copy) as T
        const newOne = {} as Record<string, unknown>
        Object.entries(value as object).forEach(([key, val]) => {
            newOne[key] = copy(val)
        })
        return newOne as T
    }
    return value
}

function fixMissingParts<T extends object>(actualValue: DeepPartial<T>, defaultValue: T): T {
    const fixedT: T = actualValue as T // We just do a cast here so we can return it at the end
    for (const key in defaultValue) {
        if(!(key in actualValue)) {
            actualValue[key] = copy(defaultValue[key])
        }
        const realField = actualValue[key] as unknown
        const expectedField = defaultValue[key]
        if(realField == null || typeof realField !== "object") continue
        if(typeof realField === "object" && typeof expectedField === "object") {
            fixMissingParts(realField, expectedField as object)
        }
        fixedT[key] = realField as T[Extract<keyof T, string>]
    }
    return fixedT
}

interface Props<T> {
    storageKey: string,
    defaultValue: T,
    dependencies?: AtomDependency<unknown>[]
    storage?: ReturnType<typeof createJSONStorage<T>>,
    sameCheck?: (oldValue: T, newValue: T) => boolean
}
export function smartAtom<T extends object>({storageKey, defaultValue, dependencies, storage, sameCheck}: Props<T>): [WritableAtom<T, [SetterVal<T>], void>, AtomDependency<T>] {
    const callbacks: Callback<T>[] = []
    const underlyingAtom = atomWithStorage<T>(storageKey, defaultValue, storage)
    const exposedAtom = atom(
        (get) => fixMissingParts(get(underlyingAtom), defaultValue),
        (get, set, newValueIn: SetterVal<T>) => {
            const newValue = resolveSetterVal(fixMissingParts(get(underlyingAtom), defaultValue), newValueIn)
            if(!sameCheck || !sameCheck(get(underlyingAtom), newValue)) {
                callbacks.forEach(callback => callback(set, newValue))
            }
            set(underlyingAtom, newValue)
        }
    )
    dependencies?.forEach(addCallback => {
        addCallback((set) => set(exposedAtom, defaultValue))
    })
    const addCallback: AtomDependency<T> = (callback: Callback<T>) => {
        callbacks.push(callback)
    }
    return [exposedAtom, addCallback]
}